// Default document-date window for the ThaiSmartTax document lists
// (ภาษีขาย / ภาษีซื้อ / ภาษีซื้อเฉลี่ย / ยกเลิกรายการ).
//
// Why this exists: those lists used to open with an empty date filter, so the
// page subscribed to the company's entire `DocsThaiSmartTax` collection —
// 41 MB and 96 s before the first 50 rows appeared for the largest tenant.
// Every entry point that seeds or resets the filter card goes through here so
// the list cannot fall back to an unbounded query by accident.
//
// The window is bounded on the past side only: it starts on the 1st of last
// month and has NO end date. That matches the ภ.พ.30 filing workflow - during
// the first half of month M the accountant is still working on month M-1 -
// while leaving documents that were entered ahead of time visible. Companies
// routinely create invoices dated months in the future (a list opened in
// August legitimately shows 1 ต.ค. documents at the top, sorted newest first),
// and an end date would silently hide every one of them.
//
// Only the past needs a bound anyway: that is where years of history pile up.
// What lies ahead of today is limited to whatever has been entered in advance.

import moment from "moment";

const DATE_FORMAT = "YYYY-MM-DD";

// Whole months before the current one that the default window starts at.
export const DEFAULT_MONTHS_BACK = 1;

// The Firestore range is widened by this much on each side so it is always a
// superset of the exact client-side filter. `docTaxDate` is stamped at
// midnight in the *writer's* timezone (the API builds it with
// `new Date(year, month - 1, day)` on Cloud Functions, i.e. 00:00 UTC), which
// is not the same instant as midnight in the viewer's browser. A day of slack
// means no document that is visible today can be dropped by the new server
// side filter.
const QUERY_MARGIN_DAYS = 1;

// Default range as the `YYYY-MM-DD` strings the date pickers and filterData use.
export function getDefaultDocDateRange() {
  return {
    fromDate: moment()
      .subtract(DEFAULT_MONTHS_BACK, "months")
      .startOf("month")
      .format(DATE_FORMAT),
    // Deliberately open-ended - see the note above. null (not "") so the
    // v-date-picker binds to an empty value rather than an invalid date.
    toDate: null,
  };
}

// Exact day boundaries in the viewer's timezone — what the user actually asked
// for. Either side may be null when only one end of the range is filled in.
export function getDocDateBounds(fromDate, toDate) {
  return {
    from: fromDate ? moment(fromDate, DATE_FORMAT).startOf("day") : null,
    to: toDate ? moment(toDate, DATE_FORMAT).endOf("day") : null,
  };
}

// Widened boundaries, as plain Dates, for the Firestore `where` clauses.
export function getDocDateQueryBounds(fromDate, toDate) {
  const { from, to } = getDocDateBounds(fromDate, toDate);
  return {
    start: from ? from.clone().subtract(QUERY_MARGIN_DAYS, "days").toDate() : null,
    end: to ? to.clone().add(QUERY_MARGIN_DAYS, "days").toDate() : null,
  };
}
